/**
 * Frequency Database Interpolation Utility
 * Linear interpolation for frequency-dependent parameter data from database
 */

/**
 * Interpolate a parameter value at target frequency
 * @param {Array} freqData - Array of frequency data points from database
 * @param {string} paramKey - Parameter key to interpolate (e.g., 'gain_db', 'nf_db')
 * @param {number} targetFreqMHz - Target frequency in MHz
 * @returns {Object} {value: number, inRange: boolean, clampedEdge: 'low'|'high'|null}
 */
export function interpolateParameter(freqData, paramKey, targetFreqMHz) {
  if (!freqData || !Array.isArray(freqData) || freqData.length === 0) {
    console.warn('Invalid or empty frequency data');
    return { value: 0, inRange: false, clampedEdge: null };
  }

  // Sort by frequency (should already be sorted, but ensure it)
  const sortedData = [...freqData].sort((a, b) => a.frequency_mhz - b.frequency_mhz);

  const freqMin = sortedData[0].frequency_mhz;
  const freqMax = sortedData[sortedData.length - 1].frequency_mhz;

  // Return null for out-of-range frequencies (no data available)
  if (targetFreqMHz < freqMin) {
    return {
      value: null,
      inRange: false,
      clampedEdge: 'low'
    };
  }

  if (targetFreqMHz > freqMax) {
    return {
      value: null,
      inRange: false,
      clampedEdge: 'high'
    };
  }

  // Exact match
  const exactMatch = sortedData.find(d => d.frequency_mhz === targetFreqMHz);
  if (exactMatch) {
    return {
      value: exactMatch[paramKey] || 0,
      inRange: true,
      clampedEdge: null
    };
  }

  // Linear interpolation
  // Find bounding indices
  let i = 0;
  while (i < sortedData.length - 1 && sortedData[i + 1].frequency_mhz < targetFreqMHz) {
    i++;
  }

  const p1 = sortedData[i];
  const p2 = sortedData[i + 1];

  const f1 = p1.frequency_mhz;
  const f2 = p2.frequency_mhz;
  const v1 = p1[paramKey] || 0;
  const v2 = p2[paramKey] || 0;

  // Interpolation factor
  const t = (targetFreqMHz - f1) / (f2 - f1);

  // Linear interpolation
  const value = v1 + t * (v2 - v1);

  return {
    value,
    inRange: true,
    clampedEdge: null
  };
}

/**
 * Interpolate all parameters at target frequency
 * @param {Array} freqData - Array of frequency data points
 * @param {number} targetFreqMHz - Target frequency in MHz
 * @returns {Object} {gain, nf, p1db, oip3, inRange, clampedEdge}
 */
export function interpolateAllParameters(freqData, targetFreqMHz) {
  const gain = interpolateParameter(freqData, 'gain_db', targetFreqMHz);
  const nf = interpolateParameter(freqData, 'nf_db', targetFreqMHz);
  const p1db = interpolateParameter(freqData, 'p1db_dbm', targetFreqMHz);
  const oip3 = interpolateParameter(freqData, 'oip3_dbm', targetFreqMHz);

  return {
    gain: gain.value,
    nf: nf.value,
    p1db: p1db.value,
    oip3: oip3.value,
    inRange: gain.inRange, // Use gain's range status (all should be same)
    clampedEdge: gain.clampedEdge
  };
}

/**
 * Pre-calculate interpolations for an array of frequencies (for sweep optimization)
 * @param {Array} freqData - Array of frequency data points
 * @param {Array<number>} frequencies - Array of target frequencies in MHz
 * @returns {Object} Map of {frequency: {gain, nf, p1db, oip3, inRange, clampedEdge}}
 */
export function preCalculateFreqDataInterpolations(freqData, frequencies) {
  const results = {};

  frequencies.forEach(freq => {
    results[freq] = interpolateAllParameters(freqData, freq);
  });

  return results;
}

/**
 * Get frequency range from frequency data
 * @param {Array} freqData - Array of frequency data points
 * @returns {Object} {min: number, max: number, unit: 'MHz'} or null
 */
export function getFreqDataRange(freqData) {
  if (!freqData || !Array.isArray(freqData) || freqData.length === 0) {
    return null;
  }

  const sortedData = [...freqData].sort((a, b) => a.frequency_mhz - b.frequency_mhz);

  return {
    min: sortedData[0].frequency_mhz,
    max: sortedData[sortedData.length - 1].frequency_mhz,
    unit: 'MHz'
  };
}

/**
 * Validate frequency data structure
 * @param {Array} freqData - Frequency data to validate
 * @returns {Object} {valid: boolean, errors: Array<string>}
 */
export function validateFreqData(freqData) {
  const errors = [];

  if (!freqData) {
    errors.push('Frequency data is null or undefined');
    return { valid: false, errors };
  }

  if (!Array.isArray(freqData)) {
    errors.push('Frequency data is not an array');
    return { valid: false, errors };
  }

  if (freqData.length === 0) {
    errors.push('Frequency data array is empty');
    return { valid: false, errors };
  }

  // Check first data point structure
  const firstPoint = freqData[0];
  if (!firstPoint.frequency_mhz) {
    errors.push('Missing frequency_mhz field');
  }

  // Check for required parameter fields (at least one should exist)
  const hasAnyParam = firstPoint.gain_db !== undefined ||
                       firstPoint.nf_db !== undefined ||
                       firstPoint.p1db_dbm !== undefined ||
                       firstPoint.oip3_dbm !== undefined;

  if (!hasAnyParam) {
    errors.push('No parameter data found (gain_db, nf_db, p1db_dbm, oip3_dbm)');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get closest voltage from available voltages
 * @param {number} targetVoltage - Target voltage
 * @param {Array<number>} availableVoltages - Array of available voltages
 * @returns {number} Closest voltage
 */
export function getClosestVoltage(targetVoltage, availableVoltages) {
  if (!availableVoltages || availableVoltages.length === 0) {
    return targetVoltage;
  }

  if (availableVoltages.length === 1) {
    return availableVoltages[0];
  }

  return availableVoltages.reduce((prev, curr) =>
    Math.abs(curr - targetVoltage) < Math.abs(prev - targetVoltage) ? curr : prev
  );
}

export default {
  interpolateParameter,
  interpolateAllParameters,
  preCalculateFreqDataInterpolations,
  getFreqDataRange,
  validateFreqData,
  getClosestVoltage
};
